import { useState, useEffect } from 'react'
import { useRouter } from 'next/router'
import { setAnalyticsId } from '@utils/analytics'
import { resetStoredData, signupMetrics } from '@store/app/actions'
import { apply, clearApplicantError, getZipMarkets, clearMarkets } from '@store/applicant/actions'
import { EXISTING_ACCOUNT_URL, CREATE_PASSWORD_URL } from '@helpers/urls'
import {
  selectNewCustomer,
  selectApplicantCreated,
  selectError,
  selectErrors,
  selectValidMarkets
} from '@store/applicant/reducer'
import SiteHeader from '@components/SiteHeader'
import OutOfAreaModal from '@components/OutOfAreaModal'
import {
  SignUpStep1,
  SignUpStep2,
  Footer,
  SelectMarket,
  DownloadApp,
  Heb,
  BackToTop,
  Faq,
  PayBreakdown,
  WhyDeliver,
  Testimonials,
  Cities,
  ShopAndDeliver,
  ByTexans
} from '@components/home'
import { Main } from '@src/shared/styles/home'
import { useMountEffect } from '@utils/hooks'
import { selectLoading } from '@store/requests/reducer'
import { useAppDispatch, useAppSelector } from '@src/client/store'

const Home = () => {
  const router = useRouter()
  const { query } = router
  const dispatch = useAppDispatch()
  const sourceRequest = useAppSelector(state => state.sourceRequest)
  const { qs, utm, referral, platform } = sourceRequest
  const error = useAppSelector(selectError)
  const errors = useAppSelector(selectErrors)
  const created = useAppSelector(selectApplicantCreated)
  const newCustomer = useAppSelector(selectNewCustomer)
  const markets = useAppSelector(selectValidMarkets)
  const loading = useAppSelector(selectLoading)
  const [mounted, setMounted] = useState(false)

  const [analyticsId, setAnalyticsIdState] = useState(null)

  useMountEffect(() => {
    setMounted(true)
    const id = setAnalyticsId()
    setAnalyticsIdState(id)
    signupMetrics({
      id,
      event: 'landing_page'
    })
    dispatch(clearApplicantError())
    dispatch(resetStoredData())
  })

  useEffect(() => {
    if (created) {
      window.scrollTo(0, 0)
      dispatch(signupMetrics({ event: 'submit_application', data: { zipcode: query.zip } }))
      router.push(newCustomer ? CREATE_PASSWORD_URL : EXISTING_ACCOUNT_URL)
    }
  }, [created, newCustomer, query.zip, dispatch])

  // control apply step with location state
  useEffect(() => {
    if (query.zip) {
      // if zip is set retrieve markets
      dispatch(getZipMarkets(query.zip))
    } else {
      // if not clear markets
      dispatch(clearMarkets())
    }
  }, [query.zip, dispatch])

  useEffect(() => {
    if (error || errors) {
      window.scrollTo(0, 0)
    }
  }, [error, errors])

  const updateQuery = (zip = undefined, marketId = undefined) => {
    const params = new URLSearchParams()
    if (zip) params.set('zip', zip)
    if (marketId) params.set('marketId', String(marketId))
    Object.entries(router.query).forEach(([key, value]) => {
      if (key.indexOf('utm_') === 0 && typeof value === 'string') {
        params.set(key, value)
      }
    })
    const paramsStr = params.toString()
    router.push(paramsStr ? `/?${paramsStr}` : '/')
  }

  const [userMarket, setUserMarket] = useState(null)
  useEffect(() => {
    if (markets && markets.length === 0) {
      // zipcode is out of zone
      dispatch(
        signupMetrics({
          event: 'out-of-zone',
          data: { zipcode: query.zip }
        })
      )
    } else if (markets?.length && markets[0].distance === 0) {
      // select first market if zip matches
      setUserMarket(markets[0])
      dispatch(
        signupMetrics({
          event: 'in-zone',
          data: { distance: markets[0].distance, zipcode: query.zip }
        })
      )
    } else if (markets?.length && query.marketId) {
      // select market if selected from list
      const market = markets.find(m => m.id === query.marketId)
      setUserMarket(market)
      dispatch(
        signupMetrics({
          event: 'near-zone',
          data: { distance: market.distance, zipcode: query.zip }
        })
      )
    } else if (markets?.length && !query.marketId) {
      // back to market select
      setUserMarket(null)
    }
  }, [markets, query.marketId, query.zip, dispatch])

  if (!markets || markets.length === 0) {
    // display zip form
    return (
      <>
        <Main>
          <SiteHeader />
          <SignUpStep1
            onZipComplete={zip => {
              updateQuery(zip)
              dispatch(
                signupMetrics({
                  event: 'submit_zipcode',
                  data: { zipcode: zip }
                })
              )
            }}
          />

          <WhyDeliver />

          <PayBreakdown />

          <ShopAndDeliver />

          <Testimonials />

          <DownloadApp />

          <Faq />

          <ByTexans />

          <Heb />

          <Cities />

          <BackToTop />
        </Main>
        <Footer />
        {mounted ? (
          <OutOfAreaModal
            show={query.zip ? markets?.length === 0 : null}
            onClose={() => {
              updateQuery()
            }}
          />
        ) : null}
      </>
    )
  }
  if (userMarket) {
    // display sign up form
    return (
      <SignUpStep2
        onSubmit={evt => {
          dispatch(apply(evt.target))
        }}
        zipcode={query.zip}
        error={!errors && error}
        errors={errors}
        qs={qs}
        referral={referral}
        phoneType={platform}
        userMarket={userMarket}
        analyticsId={analyticsId}
        utm={utm}
        loading={loading}
      />
    )
  }
  if (markets.length) {
    // display list of markets
    return (
      <SelectMarket
        markets={markets}
        clearMarkets={() => updateQuery()}
        selectMarket={market => updateQuery(query.zip, market.id)}
      />
    )
  }
}

export default Home
